import { createStore, useStore as baseUseStore, Store } from 'vuex';
import { InjectionKey, computed } from 'vue';
import createPersistedState from 'vuex-persistedstate';
import { User } from './types/types.js';
import { aiDecisionModule } from './stores/aiDecision';

interface State {
	version: string;
	loggedin: boolean;
	user: User | null;
	unreadActivitiesCnt: number;
	searchDrawerOpen: boolean;
	defaultTablePageSize: number;
	intendedUrl: string | null;
	environment: string;
	isMobile: boolean;
	formData: {
		[formId: string]: any;
	};
}

const getDefaultState = (): State => ({
	version: '0.0.133',
	loggedin: false,
	user: null,
	unreadActivitiesCnt: 0,
	searchDrawerOpen: false,
	defaultTablePageSize: 20,
	intendedUrl: null,
	environment: 'development',
	isMobile: false,
	formData: {},
});

export const key: InjectionKey<Store<State>> = Symbol();

/**
 * Was einen Browser-Neustart ueberleben darf. Bewusst eng: der Job-Zustand der KI-Entscheidung
 * liegt als eigenes Modul daneben und gehoert nicht dazu — ein gespeicherter Job waere nach einem
 * Reload garantiert veraltet und hat die Fehlermeldung ueber Tage konserviert.
 */
const PERSISTED_PATHS: string[] = ['formData', 'user', 'loggedin', 'defaultTablePageSize'];

const store = createStore<State>({
	// Job-Zustand der KI-Entscheidung liegt als eigenes Modul daneben: es ist der erste State im
	// Projekt, der kein UI-Zustand ist, und haelt store.ts lesbar.
	modules: {
		aiDecision: aiDecisionModule,
	},
	state: getDefaultState(),
	mutations: {
		loggedin(state, payload: boolean) {
			state.loggedin = payload;
		},
		user(state, payload: User) {
			state.user = payload;
		},
		toggleSearchDrawerOpen(state) {
			state.searchDrawerOpen = !state.searchDrawerOpen;
		},
		setIntendedUrl(state, url: string | null) {
			state.intendedUrl = url;
		},
		setEnvironment(state, env: string) {
			state.environment = env;
		},
		setIsMobile(state, env: string) {
			state.isMobile = env;
		},
		saveFormData(state, { formId, data }: { formId: string; data: any }) {
			state.formData = {
				...state.formData,
				[formId]: data,
			};
		},
		clearFormData(state, formId: string) {
			const newFormData = { ...state.formData };
			delete newFormData[formId];
			state.formData = newFormData;
		},
		clearAllFormData(state) {
			state.formData = {};
		},
	},
	actions: {
		toggleSearchDrawerOpen(context, payload) {
			context.commit('toggleSearchDrawerOpen');
		},
		setUser({ commit }, payload: User) {
			commit('user', payload);
		},
		login({ commit }, payload: boolean) {
			commit('loggedin', payload);
		},
		logout({ commit }) {
			commit('user', null);
			commit('loggedin', false);
			commit('clearAllFormData');
		},
		setIntendedUrl({ commit }, url: string | null) {
			commit('setIntendedUrl', url);
		},
		setEnvironment({ commit }, env: string) {
			commit('setEnvironment', env);
		},
		setIsMobile({ commit }, val: string) {
			commit('setIsMobile', val);
		},
		saveFormData({ commit }, payload: { formId: string; data: any }) {
			commit('saveFormData', payload);
		},
		clearFormData({ commit }, formId: string) {
			commit('clearFormData', formId);
		},
		clearAllFormData({ commit }) {
			commit('clearAllFormData');
		},
	},
	getters: {
		getFormData: (state) => (formId: string) => {
			return state.formData[formId] || null;
		},
		getAllFormData: (state) => {
			return state.formData;
		},
	},
	plugins: [
		createPersistedState({
			storage: window.localStorage,
			paths: PERSISTED_PATHS,
			/**
			 * Der eigene reducer ersetzt in vuex-persistedstate den eingebauten Picker vollstaendig —
			 * paths wird ihm nur als zweites Argument angeboten. Wer es ignoriert, schreibt den
			 * gesamten Root-State ins localStorage, inklusive der Modul-States. Deshalb wird hier
			 * zuerst auf die Allowlist reduziert und erst danach das formData-Sonderverhalten
			 * angewendet.
			 */
			reducer: (state) => {
				const persisted: Record<string, any> = {};
				for (const path of PERSISTED_PATHS) {
					persisted[path] = (state as Record<string, any>)[path];
				}
				if (!state.loggedin) {
					persisted.formData = {};
				}
				return persisted;
			},
		}),
	],
});

export function useStore() {
	return baseUseStore(key);
}

export const authUser = computed(() => {
	if (!store.state.loggedin || store.state.user === null) {
		console.error('User not logged in');
		throw new Error('User not logged in');
	}
	return store.state.user;
});

export default store;
